import React from 'react'
import { mergeClassnames } from '@mote/helpers'
import { __DEV__ } from '@mote/helpers/constants'
import styled, { css, th } from '@xstyled/styled-components'
import { TableContext } from './table.context'
import { TableGenericContext } from './tableGeneric.context'

export const TableCell = React.forwardRef(
  (
    {
      className,
      align = 'inherit',
      as: asProp,
      scope: scopeProp,
      sortDirection,
      variant: variantProp,
      ...props
    },
    ref
  ) => {
    const table = React.useContext(TableContext)
    const tableHead = React.useContext(TableGenericContext)

    const isHeadCell = tableHead?.variant === 'head'

    let as
    if (asProp) {
      as = asProp
    } else {
      as = isHeadCell ? 'th' : 'td'
    }

    let scope = scopeProp
    if (!scope && isHeadCell) {
      scope = 'col'
    }

    const variant = variantProp || tableHead?.variant

    let ariaSort = null
    if (sortDirection) {
      ariaSort = sortDirection === 'asc' ? 'ascending' : 'descending'
    }

    return (
      <TableCellRoot
        as={as}
        className={mergeClassnames('mote-table-cell', className)}
        ref={ref}
        aria-sort={ariaSort}
        scope={scope}
        align={align}
        sortDirection={sortDirection}
        variant={variant}
        borderless={table?.borderless}
        size={table?.size}
        {...props}
      />
    )
  }
)

if (__DEV__) {
  TableCell.displayName = 'TableCell'
}

const TableCellRoot = styled.tdBox`
  font-family: sans;
  font-size: 15px;
  font-weight: 400;
  line-height: 120%;
  color: black;
  display: table-cell;
  vertical-align: inherit;
  outline: 0;
  text-align: left;
  height: 48px;

  ${({ size }) => {
    switch (size) {
      case 'sm':
        return css`
          padding: 0 16px;
        `
      case 'lg':
        return css`
          padding: 0 32px;
        `
      default:
        return css`
          padding: 0 24px;
        `
    }
  }}

  ${({ borderless }) =>
    borderless
      ? css`
          &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }

          &:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
        `
      : css`
          border-bottom: 1px solid ${th.color('grey.l2')};
        `};

  ${({ variant }) => {
    switch (variant) {
      case 'head':
        return css`
          font-weight: 500;
          background-color: ${th.color('grey.l4')};
        `
      case 'footer':
        return css`
          font-size: 12px;
          color: ${th.color('grey.d2')};
        `
    }
  }};

  ${({ align }) => {
    switch (align) {
      case 'center':
        return css`
          text-align: center;
        `
      case 'right':
        return css`
          text-align: right;
        `
      case 'left':
        return css`
          text-align: left;
        `
      case 'justify':
        return css`
          text-align: justify;
        `
    }
  }};
`
